import {
    AuthContext,
    DataTable,
    DataTablePresenter,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderClaimEligibilitiesRequest,
    ProviderClaimEligibility,
    renderDate,
    renderString,
} from '@enter/core';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useRef } from 'react';
import { Row } from 'react-table';
import { DialogContext } from 'services';
import { parseSortBy, RootStore } from 'stores';
import { EligibilityDetail } from '../claims/components/claim-timeline-table/eligibility-detail';
import * as S from './eligibility.styles';

const columns: ITableColumn<ProviderClaimEligibility>[] = [
    {
        id: 'Name',
        Header: 'Insured Name',
        accessor: 'first',
        disableSortBy: true,
        Cell: ({ row }) => <S.Cell>{_([row.original.first, row.original.last]).compact().join(' ')}</S.Cell>,
        minWidth: 250,
    },
    {
        id: 'PlanID',
        Header: 'Plan ID',
        accessor: 'planId',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderString(cell.value)}</S.Cell>,
        minWidth: 250,
    },
    {
        id: 'GroupID',
        Header: 'Group ID',
        accessor: 'groupId',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderString(cell.value)}</S.Cell>,
        minWidth: 250,
    },
    {
        id: 'DOB',
        Header: 'DOB',
        accessor: 'dob',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderDate(cell.value)}</S.Cell>,
        show: false,
    },
];

const filterSchemas: IFilterSchema<keyof ListProviderClaimEligibilitiesRequest>[] = [
    {
        label: 'Claim ID',
        filterId: 'filterClaimId',
        columnId: 'claimId',
        filterType: 'String',
    },
    {
        label: 'Created',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
    {
        label: 'Created',
        filterId: 'filterTypeParameter',
        filterType: 'String',
        columnId: 'Created',
    },
];

type EligibilityProps = {};

type IInjectedProps = EligibilityProps & { rootStore: RootStore };

export const Eligibility = inject('rootStore')(
    observer((props: EligibilityProps) => {
        const { rootStore } = props as IInjectedProps;
        if (!rootStore) {
            return <></>;
        }

        const [loading, setLoading] = React.useState(false);
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);

        const presenter = useRef(
            new DataTablePresenter({
                name: 'Eligibility',
                data: [],
                columns,
                filterSchemas,
            }),
        ).current;

        const onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
            try {
                setLoading(true);
                authContext.cancelGroup('documents');
                authContext.setGroup('documents');

                const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
                const sortAndOrder = parseSortBy(sortBy, columns);
                const listing = await rootStore.eligibilityStore.loadEligibilityListing({
                    page: pageIndex,
                    pageSize,
                    ...sortAndOrder,
                    filters: validFilters,
                });
                presenter.onDataUpdate(listing.eligibilities || []);
                presenter.onDataTotalChange(listing.total || 0);

                setLoading(false);
            } catch (err) {
                dialogContext.showError(err);
            }
        };

        const renderRowSubComponent = (row: Row<ProviderClaimEligibility>): React.ReactElement => {
            return (
                <S.Expand>
                    <EligibilityDetail eligibility={row.original} />
                </S.Expand>
            );
        };

        const onRowClick = async (row: Row<ProviderClaimEligibility>) => {
            row.toggleRowExpanded();
        };

        return (
            <PageLayout>
                <PageHeader title="Eligiblity" hideDateRange={true} />
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable
                            presenter={presenter}
                            isLoading={loading}
                            onFetch={onFetch}
                            onRowClick={onRowClick}
                            renderRowSubComponent={renderRowSubComponent}
                        />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }),
);
